import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { YPageShell, YPageShellBody } from '../../../ui/shell/YPageShell';
import { YButton } from '../../../ui/YButton';
import { PlaceSearchInput } from '../../../components/ui/PlaceSearchInput';
import { JoinByCodeWidget } from '../../clubs/components/JoinByCodeWidget';
import { FindClubCard } from '../../clubs/components/FindClubCard';
import { FindClubFeaturedCard } from '../../clubs/components/FindClubFeaturedCard';
import { FindClubMapView } from '../../clubs/components/FindClubMapView';
import { discoverClubs } from '../../clubs/services/findClubService';
import { CommunityShellTabs } from '../components/CommunityShellTabs';
import './find-a-club-page.css';

const RADIUS_MIN = 5;
const RADIUS_MAX = 80;
const DEFAULT_RADIUS = 40;

const SORT_OPTIONS = [
  { value: 'distance', label: 'NEAREST' },
  { value: 'dues', label: 'LOWEST DUES' },
  { value: 'acres', label: 'MOST ACREAGE' },
  { value: 'openings', label: 'MOST OPENINGS' },
];

const SORT_META = {
  distance: 'NEAREST FIRST',
  dues: 'LOWEST DUES',
  acres: 'MOST ACREAGE',
  openings: 'MOST OPENINGS',
};

export function FindAClubPage() {
  const [placeQuery, setPlaceQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [acceptingOnly, setAcceptingOnly] = useState(false);
  const [sort, setSort] = useState('distance');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeClubId, setActiveClubId] = useState(null);
  const cardRefs = useRef({});

  const scrollToClub = useCallback((clubId) => {
    const node = cardRefs.current[clubId];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const handleClubSelect = useCallback((clubId) => {
    setActiveClubId(clubId);
    scrollToClub(clubId);
  }, [scrollToClub]);

  const runSearch = useCallback(async () => {
    if (!selectedPlace) {
      setError('Select a city or town to search near.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await discoverClubs({
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
        radiusMiles: radius,
        acceptingOnly,
        keyword,
        sort,
      });
      setResults(Array.isArray(data?.results) ? data.results : []);
      setMeta(data?.meta || null);
      setHasSearched(true);
      setActiveClubId(null);
    } catch (_) {
      setResults([]);
      setMeta(null);
      setError('Unable to load clubs right now. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [acceptingOnly, keyword, radius, selectedPlace, sort]);

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch();
  };

  const promoted = results.filter((club) => club.is_promoted);
  const regular = results.filter((club) => !club.is_promoted);

  const placeLabel = useMemo(() => {
    const raw = selectedPlace?.label || selectedPlace?.name || placeQuery || 'NOWHERE';
    return String(raw).toUpperCase();
  }, [placeQuery, selectedPlace]);

  const resultCount = meta?.count ?? results.length;
  const radiusLabel = meta?.radius_miles != null ? meta.radius_miles : radius;

  const renderClubCard = (club, { featured = false } = {}) => (
    <div
      key={club.id}
      ref={(node) => {
        if (node) cardRefs.current[club.id] = node;
      }}
    >
      {featured ? (
        <FindClubFeaturedCard club={club} highlighted={activeClubId === club.id} />
      ) : (
        <FindClubCard
          club={club}
          variant="row"
          highlighted={activeClubId === club.id}
        />
      )}
    </div>
  );

  return (
    <YPageShell testId="find-a-club-page" className="fac-page fac-page--field">
      <CommunityShellTabs />
      <YPageShellBody className="fac-body">
        <div className="fac-layout">
          <JoinByCodeWidget
            variant="field"
            title="Have an invite code?"
            description="Join a club instantly"
          />

          <div className="fac-search-map">
            <section className="fac-search-panel" aria-labelledby="fac-search-heading">
              <h2 id="fac-search-heading" className="fac-search-panel__title">
                Search by location
              </h2>
              <p className="fac-search-panel__intro">
                Club locations are approximate. Exact boundaries are never shown in the directory.
              </p>

              <form className="fac-search-form" onSubmit={handleSubmit}>
                <label className="fac-field" htmlFor="fac-place-search">
                  <span className="fac-field__label">Near</span>
                  <PlaceSearchInput
                    inputId="fac-place-search"
                    value={placeQuery}
                    onChange={setPlaceQuery}
                    onSelect={(place) => {
                      setSelectedPlace(place);
                      setPlaceQuery(place.label || place.name || '');
                    }}
                    placeholder="City, town, or county…"
                    aria-label="Search near a place"
                  />
                </label>

                <label className="fac-field" htmlFor="fac-radius">
                  <span className="fac-field__label fac-field__label--split">
                    <span>Radius</span>
                    <span>
                      {radius}
                      {' '}
                      MI
                    </span>
                  </span>
                  <input
                    id="fac-radius"
                    className="fac-radius"
                    type="range"
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                    step={5}
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                  />
                </label>

                <div className="fac-field">
                  <span className="fac-field__label" id="fac-sort-label">Sort by</span>
                  <div className="fac-sort-pills" role="group" aria-labelledby="fac-sort-label">
                    {SORT_OPTIONS.map((option) => {
                      const on = sort === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`fac-sort-pill${on ? ' is-on' : ''}`}
                          aria-pressed={on}
                          onClick={() => setSort(option.value)}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label className="fac-field" htmlFor="fac-keyword">
                  <span className="fac-field__label">Keyword (optional)</span>
                  <input
                    id="fac-keyword"
                    type="search"
                    className="fac-input"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Club name, county, description…"
                  />
                </label>

                <button
                  type="button"
                  className={`fac-accept${acceptingOnly ? ' is-on' : ''}`}
                  aria-pressed={acceptingOnly}
                  onClick={() => setAcceptingOnly((v) => !v)}
                  data-testid="fac-accepting-only"
                >
                  <span className="fac-accept__box" aria-hidden>
                    {acceptingOnly ? '×' : ''}
                  </span>
                  <span>Accepting members only</span>
                </button>

                {error ? <div className="fac-error" role="alert">{error}</div> : null}

                <YButton
                  type="submit"
                  variant="primary"
                  className="fac-search-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      SEARCHING…
                    </>
                  ) : (
                    'SEARCH CLUBS'
                  )}
                </YButton>
              </form>
            </section>

            <div className="fac-map-frame" aria-label="Search area map">
              {selectedPlace ? (
                <>
                  <div className="fac-map-frame__badge">
                    SEARCH AREA
                    <br />
                    {radius}
                    {' '}
                    MI ·
                    {' '}
                    {placeLabel}
                  </div>
                  <FindClubMapView
                    center={{
                      latitude: selectedPlace.latitude,
                      longitude: selectedPlace.longitude,
                    }}
                    radiusMiles={radius}
                    clubs={hasSearched ? results : []}
                    activeClubId={activeClubId}
                    onClubSelect={handleClubSelect}
                  />
                </>
              ) : (
                <div className="fac-map-frame__empty" data-testid="fac-map-empty">
                  <p>Select a city or town to preview your search radius on the map.</p>
                </div>
              )}
            </div>
          </div>

          {hasSearched ? (
            <section className="fac-results" aria-live="polite">
              <div className="fac-results__header">
                <h2 className="fac-results__title">
                  {resultCount
                    ? `${resultCount} club${resultCount === 1 ? '' : 's'} found`
                    : 'No clubs found'}
                </h2>
                <span className="fac-results__meta">
                  WITHIN
                  {' '}
                  {radiusLabel}
                  {' '}
                  MI ·
                  {' '}
                  {SORT_META[sort] || SORT_META.distance}
                </span>
              </div>

              {results.length === 0 ? (
                <p className="fac-results__empty">
                  No listed clubs match this search. Try a wider radius or a different area.
                </p>
              ) : (
                <>
                  {promoted.length > 0 ? (
                    <div className="fac-promoted" data-testid="fac-promoted">
                      <div className="fac-promoted__head">
                        <span className="fac-promoted__title">Promoted listings</span>
                        <span className="fac-promoted__sub">From club presidents</span>
                      </div>
                      <div className="fac-promoted__grid y-carousel">
                        {promoted.map((club) => renderClubCard(club, { featured: true }))}
                      </div>
                    </div>
                  ) : null}

                  {regular.length > 0 ? (
                    <div className="fac-range" data-testid="fac-range">
                      <div className="fac-range__head">Clubs in range</div>
                      <div className="fac-range__list">
                        {regular.map((club) => renderClubCard(club))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          ) : null}
        </div>
      </YPageShellBody>
    </YPageShell>
  );
}
